import CryptoJS from 'crypto-js';

/**
 * Utility class for computing file hashes.
 * Ports HashUtil.cs from the VSTO plugin.
 */
export class HashUtil {
  /**
   * Calculate MD5 hash of binary data.
   * Uses crypto-js for MD5 (Web Crypto API doesn't support MD5).
   * @param data - ArrayBuffer or Uint8Array of file content
   * @returns MD5 hash as lowercase hex string
   */
  static calculateMD5(data: ArrayBuffer | Uint8Array): string {
    const wordArray = this.arrayBufferToWordArray(data);
    const hash = CryptoJS.MD5(wordArray);
    return hash.toString(CryptoJS.enc.Hex).toLowerCase();
  }

  /**
   * Calculate SHA256 hash of binary data.
   * Uses Web Crypto API for better performance.
   * @param data - ArrayBuffer or Uint8Array of file content
   * @returns Promise resolving to SHA256 hash as lowercase hex string
   */
  static async calculateSHA256(data: ArrayBuffer | Uint8Array): Promise<string> {
    const buffer = data instanceof Uint8Array ? new Uint8Array(data).buffer as ArrayBuffer : data;
    try {
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      return this.arrayBufferToHex(hashBuffer);
    } catch {
      // Fallback to crypto-js if Web Crypto API fails
      const wordArray = this.arrayBufferToWordArray(data);
      const hash = CryptoJS.SHA256(wordArray);
      return hash.toString(CryptoJS.enc.Hex).toLowerCase();
    }
  }

  /**
   * Calculate both MD5 and SHA256 hashes in parallel.
   * @param data - ArrayBuffer or Uint8Array of file content
   * @returns Object containing both hashes
   */
  static async calculateHashes(data: ArrayBuffer | Uint8Array): Promise<{ md5: string; sha256: string }> {
    const md5 = this.calculateMD5(data);
    const sha256 = await this.calculateSHA256(data);
    return { md5, sha256 };
  }

  /**
   * Convert ArrayBuffer to hex string
   */
  private static arrayBufferToHex(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toLowerCase();
  }

  /**
   * Convert ArrayBuffer/Uint8Array to CryptoJS WordArray
   */
  private static arrayBufferToWordArray(data: ArrayBuffer | Uint8Array): CryptoJS.lib.WordArray {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    const words: number[] = [];

    for (let i = 0; i < bytes.length; i += 4) {
      words.push(
        ((bytes[i] || 0) << 24) |
          ((bytes[i + 1] || 0) << 16) |
          ((bytes[i + 2] || 0) << 8) |
          (bytes[i + 3] || 0)
      );
    }

    return CryptoJS.lib.WordArray.create(words, bytes.length);
  }
}
